<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('overtimeManagement.title')" :description="t('overtimeManagement.description')">
      <template #actions>
        <AppButton
          :disabled="selectedRequests.length === 0"
          variant="success"
          @click="showBulkApproval = true"
        >
          <CheckCircle class="h-4 w-4 mr-2" />
          {{ t('common.approve') }} ({{ selectedRequests.length }})
        </AppButton>
        <AppButton
          :disabled="selectedRequests.length === 0"
          variant="destructive"
          @click="showBulkRejection = true"
        >
          <X class="h-4 w-4 mr-2" />
          {{ t('common.reject') }} ({{ selectedRequests.length }})
        </AppButton>
        <AppButton variant="outline" @click="exportOvertimeReport">
          <Download class="h-4 w-4 mr-2" />
          {{ t('overtimeManagement.export') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stat cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard :label="t('overtimeManagement.pendingRequests')" :value="stats.pending" :icon="Clock" iconBg="bg-amber-50" iconColor="text-amber-600" />
      <AppStatCard :label="t('overtimeManagement.monthlyOvertime')" :value="`${stats.monthlyHours}h`" :icon="TrendingUp" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
      <AppStatCard :label="t('overtimeManagement.overtimeCost')" :value="formatCurrency(stats.overtimeCost)" :icon="DollarSign" iconBg="bg-blue-50" iconColor="text-blue-600" />
      <AppStatCard :label="t('overtimeManagement.activeSessions')" :value="stats.activeSessions" :icon="Users" iconBg="bg-purple-50" iconColor="text-purple-600" />
    </div>

    <!-- Filters -->
    <AppCard>
      <div class="p-4">
        <div class="flex flex-wrap gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('overtimeManagement.status') }}</label>
            <select v-model="filters.status" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">{{ t('overtimeManagement.allStatuses') }}</option>
              <option value="pending">{{ t('common.pending') }}</option>
              <option value="approved">{{ t('common.approved') }}</option>
              <option value="rejected">{{ t('common.rejected') }}</option>
              <option value="completed">{{ t('common.completed') }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('common.department') }}</label>
            <select v-model="filters.department" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">{{ t('overtimeManagement.allDepartments') }}</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('common.from') }}</label>
            <input type="date" v-model="filters.dateFrom" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('common.to') }}</label>
            <input type="date" v-model="filters.dateTo" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <AppButton @click="applyFilters">{{ t('common.apply') }}</AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Table card -->
    <AppCard>
      <!-- Select-all header row -->
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <p class="text-sm font-medium text-foreground">{{ t('overtimeManagement.overtimeRequests') }}</p>
        <label class="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            @change="toggleSelectAll"
            :checked="selectedRequests.length === filteredRequests.length && filteredRequests.length > 0"
            class="rounded border-input"
          />
          {{ t('overtimeManagement.selectAll') }}
        </label>
      </div>

      <AppTable
        :columns="tableColumns"
        :rows="paginatedRequests"
        :loading="loading"
        rowKey="id"
        :emptyText="t('overtimeManagement.noRequests')"
      >
        <!-- Checkbox -->
        <template #cell-select="{ row }">
          <input
            type="checkbox"
            :value="row.id"
            v-model="selectedRequests"
            class="rounded border-input"
            @click.stop
          />
        </template>

        <!-- Employee -->
        <template #cell-employee="{ row }">
          <div class="flex items-center gap-2.5">
            <UserAvatar :name="row.employee.name" size="sm" />
            <div class="min-w-0">
              <p class="text-sm font-medium text-foreground truncate">{{ row.employee.name }}</p>
              <p class="text-xs text-muted-foreground truncate">{{ row.employee.department }}</p>
            </div>
          </div>
        </template>

        <!-- Date & Duration -->
        <template #cell-dateDuration="{ row }">
          <div>
            <p class="text-sm text-foreground">{{ formatDate(row.date) }}</p>
            <p class="text-xs text-muted-foreground">{{ row.startTime }} – {{ row.endTime || t('overtimeManagement.ongoing') }}</p>
            <p class="text-xs font-medium text-primary mt-0.5">
              {{ row.expectedDuration }}min
              <span v-if="row.actualDuration" class="text-muted-foreground font-normal"> ({{ row.actualDuration }}min actual)</span>
            </p>
          </div>
        </template>

        <!-- Reason -->
        <template #cell-reason="{ row }">
          <p class="text-sm text-foreground line-clamp-2 max-w-xs" :title="row.reason">{{ row.reason }}</p>
        </template>

        <!-- Status -->
        <template #cell-status="{ row }">
          <div class="flex flex-col gap-1">
            <AppBadge :variant="statusVariant(row.status)">{{ getStatusText(row.status) }}</AppBadge>
            <AppBadge v-if="row.isPriority" variant="destructive" class="w-fit">
              <AlertTriangle class="h-3 w-3 mr-1" />
              {{ t('overtimeManagement.priority') }}
            </AppBadge>
          </div>
        </template>

        <!-- Cost -->
        <template #cell-cost="{ row }">
          <span class="text-sm font-semibold text-foreground">{{ formatCurrency(calculateOvertimeCost(row)) }}</span>
        </template>

        <!-- Actions -->
        <template #actions="{ row }">
          <div class="flex items-center gap-1">
            <AppButton v-if="row.status === 'pending'" size="icon-sm" variant="ghost" class="text-emerald-600" title="Approve" @click.stop="approveRequest(row)">
              <CheckCircle class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton v-if="row.status === 'pending'" size="icon-sm" variant="ghost" class="text-destructive" title="Reject" @click.stop="rejectRequest(row)">
              <X class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton size="sm" variant="outline" @click.stop="viewRequestDetails(row)">{{ t('common.view') }}</AppButton>
          </div>
        </template>
      </AppTable>

      <!-- Pagination -->
      <div v-if="totalRecords > 0" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <p class="text-sm text-muted-foreground">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, totalRecords) }} of {{ totalRecords }}
        </p>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage === 1" @click="previousPage">
            <ChevronLeft class="h-3.5 w-3.5" />
          </AppButton>
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors',
              page === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-foreground hover:bg-accent'
            ]"
          >{{ page }}</button>
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage === totalPages" @click="nextPage">
            <ChevronRight class="h-3.5 w-3.5" />
          </AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Request Details Modal -->
    <AppModal v-model="showDetailsModal" :title="t('overtimeManagement.detailsTitle')" size="lg">
      <div v-if="selectedRequest" class="space-y-5">

        <!-- Employee info -->
        <div class="rounded-lg bg-muted/50 p-4">
          <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{{ t('overtimeManagement.employeeInfo') }}</p>
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.name') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.employee.name }}</span></div>
            <div><span class="text-muted-foreground">{{ t('common.department') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.employee.department }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.employeeId') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.employee.employeeId }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.hourlyRate') }}:</span> <span class="font-medium ml-1">{{ formatCurrency(selectedRequest.employee.hourlyRate) }}/{{ t('overtimeManagement.hr') }}</span></div>
          </div>
        </div>

        <!-- Request details -->
        <div>
          <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{{ t('overtimeManagement.requestDetails') }}</p>
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div><span class="text-muted-foreground">{{ t('common.date') }}:</span> <span class="font-medium ml-1">{{ formatDate(selectedRequest.date) }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.expectedDuration') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.expectedDuration }} {{ t('overtimeManagement.min') }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.startTime') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.startTime }}</span></div>
            <div>
              <span class="text-muted-foreground">{{ t('common.status') }}:</span>
              <AppBadge class="ml-1" :variant="statusVariant(selectedRequest.status)">{{ getStatusText(selectedRequest.status) }}</AppBadge>
            </div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.mode') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.mode === 'pre_scheduled' ? t('overtimeManagement.preScheduled') : t('overtimeManagement.realtimeClock') }}</span></div>
            <div><span class="text-muted-foreground">{{ t('common.type') }}:</span> <span class="font-medium ml-1">{{ (selectedRequest.sessionType || '').replace('_', ' ') }}</span></div>
          </div>
        </div>

        <!-- Clock out details -->
        <template v-if="selectedRequest.clockOutTime">
          <div class="grid grid-cols-2 gap-3 text-sm border-t border-border pt-4">
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.clockInLabel') }}:</span> <span class="font-medium ml-1">{{ formatDateTime(selectedRequest.clockInTime) }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.clockOutLabel') }}:</span> <span class="font-medium ml-1">{{ formatDateTime(selectedRequest.clockOutTime) }}</span></div>
            <div>
              <span class="text-muted-foreground">{{ t('overtimeManagement.clockOutMethod') }}:</span>
              <span class="font-medium ml-1" :class="{
                'text-emerald-600': selectedRequest.clockOutMethod === 'manual',
                'text-amber-600': selectedRequest.clockOutMethod === 'auto_expired',
                'text-destructive': selectedRequest.clockOutMethod === 'admin_force_close'
              }">
                {{ selectedRequest.clockOutMethod === 'manual' ? t('overtimeManagement.manual') : selectedRequest.clockOutMethod === 'auto_expired' ? t('overtimeManagement.autoCapped') : selectedRequest.clockOutMethod === 'admin_force_close' ? t('overtimeManagement.adminForce') : selectedRequest.clockOutMethod || '–' }}
              </span>
            </div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.actualDuration') }}:</span> <span class="font-medium ml-1">{{ selectedRequest.actualDuration }} {{ t('overtimeManagement.min') }}</span></div>
          </div>
          <div class="grid grid-cols-2 gap-3 text-sm border-t border-border pt-4">
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.estimatedCost') }}:</span> <span class="font-medium ml-1">{{ formatCurrency(selectedRequest.estimatedCost) }}</span></div>
            <div><span class="text-muted-foreground">{{ t('overtimeManagement.actualCost') }}:</span> <span class="font-bold text-primary ml-1">{{ formatCurrency(selectedRequest.actualCost) }}</span></div>
          </div>
        </template>

        <div class="text-sm">
          <p class="text-xs text-muted-foreground mb-1">Reason</p>
          <div class="p-3 rounded-lg bg-muted/50 text-foreground">{{ selectedRequest.reason }}</div>
        </div>

        <!-- Approval form for pending -->
        <div v-if="selectedRequest.status === 'pending'" class="border-t border-border pt-4">
          <p class="text-sm font-semibold text-foreground mb-3">{{ t('overtimeManagement.approvalDecision') }}</p>
          <form @submit.prevent="submitApprovalDecision" class="space-y-4">
            <div class="flex gap-6 text-sm">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="approvalDecision.action" value="approve" class="text-primary" />
                <span class="text-emerald-600 font-medium">{{ t('common.approve') }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="approvalDecision.action" value="reject" class="text-primary" />
                <span class="text-destructive font-medium">{{ t('common.reject') }}</span>
              </label>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-foreground">{{ t('overtimeManagement.notes') }}</label>
              <textarea v-model="approvalDecision.notes" rows="3" :placeholder="t('overtimeManagement.notesPlaceholder')" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
            </div>
            <div class="flex justify-end gap-2">
              <AppButton type="button" variant="outline" @click="showDetailsModal = false">{{ t('common.cancel') }}</AppButton>
              <AppButton type="submit" :disabled="!approvalDecision.action || submittingDecision">
                {{ submittingDecision ? t('overtimeManagement.submitting') : t('overtimeManagement.submitDecision') }}
              </AppButton>
            </div>
          </form>
        </div>
      </div>
    </AppModal>

    <!-- Bulk Approve Modal -->
    <AppModal v-model="showBulkApproval" :title="t('overtimeManagement.bulkApproveTitle')" size="sm">
      <div class="space-y-4">
        <p class="text-sm text-muted-foreground">{{ t('overtimeManagement.bulkApproveDesc', { count: selectedRequests.length }) }}</p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('overtimeManagement.bulkApprovalNotes') }}</label>
          <textarea v-model="bulkApprovalNotes" rows="3" :placeholder="t('overtimeManagement.bulkNotesPlaceholder')" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showBulkApproval = false">{{ t('common.cancel') }}</AppButton>
          <AppButton variant="success" :disabled="submittingBulk" @click="performBulkApproval">
            {{ submittingBulk ? t('overtimeManagement.approving') : t('overtimeManagement.approveAll') }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Bulk Reject Modal -->
    <AppModal v-model="showBulkRejection" title="Bulk Reject Requests" size="sm">
      <div class="space-y-4">
        <p class="text-sm text-muted-foreground">You are about to reject <strong class="text-foreground">{{ selectedRequests.length }}</strong> overtime requests.</p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Rejection Reason <span class="text-destructive">*</span></label>
          <textarea v-model="bulkRejectionReason" rows="3" placeholder="Provide a reason for rejecting these requests…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showBulkRejection = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="submittingBulkReject || !bulkRejectionReason.trim()" @click="performBulkRejection">
            {{ submittingBulkReject ? 'Rejecting…' : 'Reject All' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  CheckCircle,
  Download,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-vue-next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import UserAvatar from '@/components/common/UserAvatar.vue';
import { AppPageHeader, AppStatCard, AppCard, AppTable, AppBadge, AppButton, AppModal } from '@/components/ui';

// Import services
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';
import { useCurrency } from '@/composables/useCurrency';

// Currency formatting
const { formatCurrency, loadCurrency } = useCurrency();
const { t } = useI18n();

// Reactive data
const loading = ref(false);
const overtimeRequests = ref([]);
const departments = ref([]);
const selectedRequests = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const showDetailsModal = ref(false);
const showBulkApproval = ref(false);
const selectedRequest = ref(null);
const submittingDecision = ref(false);
const submittingBulk = ref(false);
const bulkApprovalNotes = ref('');
const showBulkRejection = ref(false);
const submittingBulkReject = ref(false);
const bulkRejectionReason = ref('');
const approvalDecision = ref({ action: '', notes: '' });

const stats = ref({
  pending: 0,
  monthlyHours: 0,
  overtimeCost: 0,
  activeSessions: 0
});

const filters = ref({
  status: '',
  department: '',
  dateFrom: '',
  dateTo: ''
});

const tableColumns = computed(() => [
  { key: 'select',       label: '',             headerClass: 'w-10' },
  { key: 'employee',     label: t('overtimeManagement.name') },
  { key: 'dateDuration', label: t('common.date') + ' & ' + t('common.duration'), mobileHide: true },
  { key: 'reason',       label: t('common.reason'),       mobileHide: true },
  { key: 'status',       label: t('overtimeManagement.status') },
  { key: 'cost',         label: t('overtimeClock.cost'),  mobileHide: true },
]);

// Computed properties
const filteredRequests = computed(() => {
  let filtered = overtimeRequests.value;

  if (filters.value.status) {
    filtered = filtered.filter(req => req.status === filters.value.status);
  }

  if (filters.value.department) {
    filtered = filtered.filter(req => req.employee.departmentId === filters.value.department);
  }

  if (filters.value.dateFrom) {
    filtered = filtered.filter(req => dayjs(req.date).isAfter(dayjs(filters.value.dateFrom).subtract(1, 'day')));
  }

  if (filters.value.dateTo) {
    filtered = filtered.filter(req => dayjs(req.date).isBefore(dayjs(filters.value.dateTo).add(1, 'day')));
  }

  return filtered;
});

const totalRecords = computed(() => filteredRequests.value.length);
const totalPages = computed(() => Math.ceil(totalRecords.value / itemsPerPage.value));

const paginatedRequests = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredRequests.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, start + 4);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

const statusVariant = (status) => {
  const map = {
    pending: 'warning',
    approved: 'success',
    completed: 'success',
    active: 'default',
    under_review: 'warning',
    rejected: 'destructive',
    cancelled: 'muted',
    expired: 'muted',
  };
  return map[status] || 'muted';
};

// Methods
const loadOvertimeRequests = async () => {
  try {
    loading.value = true;
    console.log('🔄 Loading overtime requests...');

    const response = await attendanceService.overtime.admin.getOvertimeRequests({
      status: filters.value.status,
      departmentId: filters.value.department,
      dateFrom: filters.value.dateFrom,
      dateTo: filters.value.dateTo,
      page: currentPage.value,
      limit: itemsPerPage.value
    });

    const respData = response.data.data;
    const items = respData?.sessions || respData?.requests || [];
    console.log('📦 Loading overtime sessions, count:', items.length);

    if (response.data.success && items.length > 0) {
      // Transform unified backend data to frontend format
      overtimeRequests.value = items.map(s => ({
        id: s.id,
        employee: {
          id: s.employee_id,
          name: s.employee_name || 'Unknown',
          code: s.employee_code,
          department: s.department_name || 'No Department',
          departmentId: s.department_id,
          employeeId: s.employee_code,
          hourlyRate: 25,
          avatar: null
        },
        date: s.scheduled_date || (s.timer_start_time ? dayjs(s.timer_start_time).format('YYYY-MM-DD') : dayjs(s.created_at).format('YYYY-MM-DD')),
        startTime: s.scheduled_start_time || (s.timer_start_time ? dayjs(s.timer_start_time).format('HH:mm') : '-'),
        endTime: s.scheduled_end_time || (s.timer_end_time ? dayjs(s.timer_end_time).format('HH:mm') : null),
        expectedDuration: s.requested_duration_minutes || s.estimated_duration_minutes,
        actualDuration: s.actual_duration_minutes,
        clockInTime: s.clock_in_timestamp,
        clockOutTime: s.clock_out_timestamp,
        clockOutMethod: s.clock_out_method,
        reason: s.reason,
        status: s.status,
        mode: s.mode,
        priority: s.priority,
        isPriority: s.priority === 'high' || s.priority === 'urgent',
        rejectedReason: s.rejected_reason,
        sessionType: s.session_type,
        estimatedCost: s.estimated_cost,
        actualCost: s.actual_cost,
        rateMultiplier: s.overtime_rate_multiplier,
        approvedAt: s.approved_at,
        createdAt: s.created_at
      }));

      console.log('✅ Overtime sessions loaded:', overtimeRequests.value.length);
    } else {
      overtimeRequests.value = [];
      console.log('⚠️ No overtime sessions found');
    }
  } catch (error) {
    console.error('❌ Error loading overtime requests:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack
    });
    overtimeRequests.value = [];
    showToast('error', 'Failed to load overtime requests');
  } finally {
    loading.value = false;
  }
};

const loadStats = async () => {
  try {
    console.log('📊 Loading overtime stats...');
    const response = await attendanceService.overtime.admin.getOvertimeStats();

    const statsData = response.data?.data?.overview || response.data?.data || response.data;
    if (statsData) {
      stats.value = {
        pending: statsData.pending || statsData.pendingCount || 0,
        monthlyHours: statsData.monthlyHours || parseFloat(statsData.totalHours || 0).toFixed(1),
        overtimeCost: statsData.overtimeCost || statsData.totalCost || 0,
        activeSessions: statsData.activeSessions || statsData.activeCount || 0
      };
      console.log('✅ Overtime stats loaded:', stats.value);
    }
  } catch (error) {
    console.error('❌ Error loading stats:', error);
  }
};

const loadDepartments = async () => {
  try {
    console.log('🏢 Loading departments...');
    const departments_list = await adminService.getDepartments();
    departments.value = departments_list || [];
    console.log('✅ Departments loaded:', departments.value.length);
  } catch (error) {
    console.error('❌ Error loading departments:', error);
    departments.value = [];
  }
};

const approveRequest = async (request) => {
  if (!confirm(`Approve overtime request for ${request.employee.name}?`)) return;

  try {
    console.log('✅ Approving overtime request:', request.id);
    const response = await attendanceService.overtime.admin.approveOvertimeRequest(request.id, 'Approved by admin');

    if (response.data.success) {
      await loadOvertimeRequests();
      await loadStats();
      showToast('success', 'Overtime request approved successfully!');
    } else {
      throw new Error(response.data.message || 'Failed to approve request');
    }
  } catch (error) {
    console.error('❌ Error approving request:', error);
    showToast('error', error.message || 'Failed to approve request. Please try again.');
  }
};

const rejectRequest = async (request) => {
  const reason = prompt(`Enter reason for rejecting ${request.employee.name}'s overtime request:`);
  if (!reason) return;

  try {
    console.log('❌ Rejecting overtime request:', request.id);
    const response = await attendanceService.overtime.admin.rejectOvertimeRequest(request.id, reason);

    if (response.data.success) {
      await loadOvertimeRequests();
      await loadStats();
      showToast('success', 'Overtime request rejected.');
    } else {
      throw new Error(response.data.message || 'Failed to reject request');
    }
  } catch (error) {
    console.error('❌ Error rejecting request:', error);
    showToast('error', error.message || 'Failed to reject request. Please try again.');
  }
};

const viewRequestDetails = (request) => {
  selectedRequest.value = request;
  approvalDecision.value = { action: '', notes: '' };
  showDetailsModal.value = true;
};

const submitApprovalDecision = async () => {
  try {
    submittingDecision.value = true;
    const action = approvalDecision.value.action;

    console.log(`🔄 ${action}ing overtime request:`, selectedRequest.value.id);

    let response;
    if (action === 'approve') {
      response = await attendanceService.overtime.admin.approveOvertimeRequest(
        selectedRequest.value.id,
        approvalDecision.value.notes
      );
    } else {
      response = await attendanceService.overtime.admin.rejectOvertimeRequest(
        selectedRequest.value.id,
        approvalDecision.value.notes
      );
    }

    if (response.data.success) {
      showDetailsModal.value = false;
      await loadOvertimeRequests();
      await loadStats();
      showToast('success', `Overtime request ${action}d successfully!`);
    } else {
      throw new Error(response.message || `Failed to ${action} request`);
    }
  } catch (error) {
    console.error('❌ Error submitting decision:', error);
    showToast('error', error.message || 'Failed to submit decision. Please try again.');
  } finally {
    submittingDecision.value = false;
  }
};

const performBulkApproval = async () => {
  try {
    submittingBulk.value = true;
    console.log('📦 Bulk approving overtime requests:', selectedRequests.value.length);

    await attendanceService.overtime.admin.bulkApproveOvertimeRequests(
      selectedRequests.value,
      bulkApprovalNotes.value
    );

    showBulkApproval.value = false;
    selectedRequests.value = [];
    bulkApprovalNotes.value = '';

    await loadOvertimeRequests();
    await loadStats();

    showToast('success', 'Overtime requests approved successfully!');
  } catch (error) {
    console.error('❌ Error performing bulk approval:', error);
    showToast('error', 'Failed to approve requests. Please try again.');
  } finally {
    submittingBulk.value = false;
  }
};

const performBulkRejection = async () => {
  if (!bulkRejectionReason.value.trim()) return;
  try {
    submittingBulkReject.value = true;

    await attendanceService.overtime.admin.bulkRejectOvertimeRequests(
      selectedRequests.value,
      bulkRejectionReason.value
    );

    showBulkRejection.value = false;
    selectedRequests.value = [];
    bulkRejectionReason.value = '';

    await loadOvertimeRequests();
    await loadStats();

    showToast('success', 'Overtime requests rejected successfully!');
  } catch (error) {
    console.error('Bulk rejection failed:', error);
    showToast('error', 'Failed to reject requests. Please try again.');
  } finally {
    submittingBulkReject.value = false;
  }
};

const toggleSelectAll = () => {
  if (selectedRequests.value.length === filteredRequests.value.length) {
    selectedRequests.value = [];
  } else {
    selectedRequests.value = filteredRequests.value.map(req => req.id);
  }
};

const calculateOvertimeCost = (request) => {
  // Use actual or estimated cost from backend
  if (request.actualCost) return parseFloat(request.actualCost);
  if (request.estimatedCost) return parseFloat(request.estimatedCost);

  // Fallback calculation
  const hourlyRate = request.employee.hourlyRate || 25;
  const overtimeMultiplier = request.rateMultiplier || 1.5;
  const hours = (request.actualDuration || request.expectedDuration || 0) / 60;

  return hourlyRate * overtimeMultiplier * hours;
};

const formatDate = (date) => {
  return dayjs(date).format('MMM D, YYYY');
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '-';
  return dayjs.utc(timestamp).format('MMM D, h:mm A');
};

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    active: 'bg-blue-100 text-blue-800',
    under_review: 'bg-orange-100 text-orange-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
    expired: 'bg-gray-100 text-gray-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const getStatusText = (status) => {
  const texts = {
    pending: 'Pending',
    approved: 'Approved (Awaiting Activation)',
    completed: 'Completed',
    active: 'Active',
    under_review: 'Under Review',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    expired: 'Expired'
  };
  return texts[status] || status;
};

const applyFilters = () => {
  currentPage.value = 1;
  // Filters are applied automatically via computed property
};

const exportOvertimeReport = async () => {
  try {
    console.log('📤 Exporting overtime report...');

    const response = await attendanceService.overtime.admin.exportOvertimeReport({
      ...filters.value,
      format: 'xlsx'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `overtime-report-${dayjs().format('YYYY-MM-DD')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('✅ Overtime report exported successfully');
    showToast('success', 'Overtime report exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting report:', error);
    showToast('error', 'Failed to export report. Please try again.');
  }
};

// Pagination methods
const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToPage = (page) => {
  currentPage.value = page;
};

// Toast notification helper
const showToast = (type, message) => {
  if (type === 'success') {
    console.log('✅', message);
  } else {
    console.error('❌', message);
  }
  // Simple alert for now - could be enhanced with proper toast component
  alert(message);
};

// Lifecycle
onMounted(() => {
  console.log('🔄 OvertimeManagement mounted, loading data...');
  loadCurrency(); // Load currency settings
  loadOvertimeRequests();
  loadStats();
  loadDepartments();
});
</script>
